import { z } from "zod";
import { User } from "../../../context/AuthContext";
import { safeLocalStorage } from "../../../utils/safeStorage";

// Helper function to format ISO date to MM/DD/YYYY
function formatDobAsMMDDYYYY(dobIsoOrYYYYMMDD: string): string {
  // Handles incoming formats like YYYY-MM-DD or ISO strings
  const date = new Date(dobIsoOrYYYYMMDD);
  if (Number.isNaN(date.getTime())) return "";
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

// Minimal schema for persisted fields across pages
const persistedSchema = z.object({
  email: z.string().email().optional(),
  zipCode: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  dateOfBirth: z.string().optional(),
  sex: z.enum(["M", "F"]).optional(),
  ethnicity: z.array(z.string()).optional(),
  phone: z.string().optional(),
  phoneValid: z.boolean().optional(),
  termsAccepted: z.boolean().optional(),
  thirdPartyContactConsentGranted: z.boolean().optional(),
  trialSiteId: z.string().optional(),
  trialSiteTrialId: z.string().optional(),
  consentLocaleCode: z.string().optional(),
  thirdPartyContactConsentVersion: z.string().optional(),
});

export type PersistedContactFormState = z.infer<typeof persistedSchema>;

function buildKey(landingPageId: string) {
  return `minimatch:contact-form:${landingPageId}`;
}

export function getContactFormState(landingPageId: string): PersistedContactFormState {
  const raw = safeLocalStorage.getItem(buildKey(landingPageId));
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return persistedSchema.partial().parse(parsed);
  } catch {
    return {};
  }
}

export function setContactFormState(
  landingPageId: string,
  partial: Partial<PersistedContactFormState>,
) {
  const existing = getContactFormState(landingPageId);
  const merged = { ...existing, ...partial };
  safeLocalStorage.setItem(buildKey(landingPageId), JSON.stringify(merged));
}

export function clearContactFormState(landingPageId: string) {
  safeLocalStorage.removeItem(buildKey(landingPageId));
}

export function populateContactFormStateFromUser(landingPageId: string, user: User | null) {
  setContactFormState(landingPageId, {
    email: user?.email || undefined,
    firstName: user?.firstName || undefined,
    lastName: user?.lastName || undefined,
    sex: (user?.sex === "F" ? "F" : user?.sex === "M" ? "M" : undefined) || undefined,
    dateOfBirth: user?.dob ? formatDobAsMMDDYYYY(user.dob) : undefined,
    phone: user?.phoneNumber || undefined,
    phoneValid: user?.phoneNumber ? true : undefined,
    termsAccepted: false,
    thirdPartyContactConsentGranted: false,
    zipCode: user?.postalCode || undefined,
  });
}


